import React, { useEffect } from "react";
import Button from "@edaz/edazkit/Button";
import Icon, { IconType } from "@edaz/edazkit/Icon";
import { registerView, View, ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import { Col, Jumbotron, Row, Alert } from "react-bootstrap";
import StatusViewModel from "viewModels/common/payment/statusViewModel";
import AlertWithInfoIcon from "views/common/alertWithInfoIcon/alertWithInfoIcon";
import LoadingModal from "../loadingModal/loadingModal";
import classNames from "classnames";

const Single: ViewComponent<StatusViewModel> = observer(({ vm }) => {
  const tg = (code: string) =>
    vm.localizationService.translateGeneral(`eshop.payment.status.${vm.isSuccess ? "success" : "deferred"}.${code}`);
  const tgSum = (code: string) => vm.localizationService.translateGeneral(`eshop.order.summary.${code}`);

  return (
    <Row>
      <Col sm={12} md={6} className="">
        <Col sm={6} className="col-sm-12 mb-5 p-0">
          <Jumbotron className="d-flex justify-content-center align-items-center card-box-shadow mb-3 mb-md-0 p-5 p-lg-4 p-xl-7 text-center">
            <Col>
              <Icon
                className={classNames("mb-4 mt-3 mt-lg-0", {
                  "text-success": vm.isSuccess,
                  "text-orange": !vm.isSuccess,
                })}
                icon={vm.isSuccess ? IconType.CircleCheck : IconType.CircleQuestionmark}
                height={80}
                width={80}
              />
              <h5>{tg("status")}</h5>
              <div className="mb-4 px-3">{tg("statusInfo")}</div>
              {vm.isSuccess ? (
                <Button onClick={vm.getPaymentConfirmation} className="d-block mx-auto mt-3">
                  {tg("showConfirmation")}
                </Button>
              ) : (
                <AlertWithInfoIcon variant="warning" className="mt-2">
                  <span>{tg("warning")}</span>
                </AlertWithInfoIcon>
              )}
            </Col>
          </Jumbotron>
        </Col>
        <Col sm={6} className="col-sm-12 mb-5 p-0">
          {vm.orderSummaryViewModel.displayAuthCode && (
            <div className="card-box-shadow mb-0 mb-md-0 mb-sm-3 p-5 p-md-7 h-100 jumbotron">
              <h5 className="mb-5 text-center">{tgSum("authorizationCode")}</h5>
              <div className="mb-5 p-0">
                <Alert
                  variant={"light"}
                  className="d-flex justify-content-center align-items-center border-none text-center text-dark"
                  style={{ minHeight: "5rem" }}>
                  <div className={"font-weight-normal text-nowrap"} style={{ fontSize: "1.75rem" }}>
                    {vm.orderSummaryViewModel.authorizationCode}
                  </div>
                </Alert>
              </div>
              <div className="d-flex justify-content-center text-center text-md-left">
                <span className="flex-nowrap">{tgSum("authorizationCodeInfo")}</span>
              </div>
            </div>
          )}
        </Col>
      </Col>
      <Col sm={12} md={6} className="">
        <Col sm={6} className="col-sm-12 mb-5 p-0">
          <View vm={vm.orderSummaryViewModel} />
        </Col>

        <Col sm={6} className="col-sm-12 mb-5 p-0">
          <View vm={vm.ratingViewModel} />
        </Col>
      </Col>
      <div>
        <LoadingModal translate={tg} isVisible={vm.showLoadingModal} />
      </div>
    </Row>
  );
});

const Multiple: ViewComponent<StatusViewModel> = observer(({ vm }) => {
  const tg = (code: string) =>
    vm.localizationService.translateGeneral(`eshop.payment.status.${vm.isSuccess ? "success" : "deferred"}.${code}`);
  const ts = (code: string) => vm.localizationService.translateGeneral(`eshop.order.summary.${code}`);

  return (
    <Row>
      <Col sm={12} md={6} className="mb-5">
        <Jumbotron className="d-flex justify-content-center align-items-center card-box-shadow mb-0 p-3 p-lg-4 p-xl-7 text-center">
          <Col>
            <Icon
              className={classNames("mb-4 mt-3 mt-lg-0", {
                "text-success": vm.isSuccess,
                "text-orange": !vm.isSuccess,
              })}
              icon={vm.isSuccess ? IconType.CircleCheck : IconType.CircleQuestionmark}
              height={80}
              width={80}
            />
            <h5>{tg("status")}</h5>
            <div className="mb-4 px-3">
              {ts("multiEshopEmailNote")} {vm.chargeOrder.emailAddress}
            </div>
            <div className="mb-4 px-3">{ts("multiEshopLoadNote")}</div>
          </Col>
        </Jumbotron>
      </Col>
      <Col sm={12} md={6} className="mb-5">
        <View vm={vm.ratingViewModel} />
      </Col>
      <div>
        <LoadingModal translate={tg} isVisible={vm.showLoadingModal} />
      </div>
    </Row>
  );
});

const StatusView: ViewComponent<StatusViewModel> = observer(({ vm }) =>
  vm.isMultiple ? <Multiple vm={vm} /> : <Single vm={vm} />
);

registerView(StatusView, StatusViewModel);
